const signerCompletionFromSummary = (document, signer) => {
  const signerId = signer?.id || document?.current_signer?.id
  const signers = document?.assignment?.summary?.signers

  if (!signerId || !Array.isArray(signers)) return undefined

  const completed = signers.find((candidate) => candidate.id === signerId)?.completed
  return typeof completed === "boolean" ? completed : undefined
}

const currentSignerCompletion = (document, signer) => {
  if (typeof document?.current_signer?.completed === "boolean") {
    return document.current_signer.completed
  }

  return signerCompletionFromSummary(document, signer)
}

const documentUnavailableStatus = () => ({
  key: "document_unavailable",
  variant: "warning",
  icon: "icon-warning",
  title: "Documento indisponível",
  message:
    "Este documento não está mais disponível para assinatura. Se precisar de ajuda, entre em contato com o remetente.",
})

const terminalDocumentStatuses = {
  expired: {
    key: "document_expired",
    title: "Documento expirado",
    message: "O prazo para assinar este documento expirou.",
  },
  rejected_by_signer: {
    key: "document_declined",
    title: "Documento recusado",
    message: "Este documento foi recusado e não está mais disponível para assinatura.",
  },
  rejected_by_user: {
    key: "document_cancelled",
    title: "Documento cancelado",
    message: "Este documento foi cancelado e não está mais disponível para assinatura.",
  },
  failed: {
    key: "document_failed",
    title: "Documento indisponível",
    message: "Não foi possível concluir o processamento deste documento.",
  },
}

const terminalDocumentStatus = (status) => {
  const content = terminalDocumentStatuses[status]

  return content
    ? {
        ...content,
        variant: "warning",
        icon: "icon-warning",
      }
    : undefined
}

export const resolveSigningLinkStatus = (document, signer) => {
  if (document?.status === "certificated") {
    return {
      key: "document_completed",
      variant: "success",
      icon: "icon-check",
      title: "Documento concluído",
      message: "Sua assinatura foi realizada e todas as etapas deste documento foram concluídas.",
    }
  }

  const terminalStatus = terminalDocumentStatus(document?.status)
  if (terminalStatus) return terminalStatus

  if (document?.status === "pending_identity_review") {
    return {
      key: "identity_review_pending",
      variant: "success",
      icon: "icon-check",
      title: "Sua assinatura já foi concluída",
      message:
        "O documento aguarda a revisão de identidade pelo remetente antes da certificação. Você pode consultá-lo, mas não precisa assinar novamente.",
    }
  }

  if (
    typeof document?.status === "string" &&
    document.status !== "pending_signature" &&
    document.status !== "certificating" &&
    document.status !== "pending_identity_review"
  ) {
    return documentUnavailableStatus()
  }

  if (document?.is_closed) {
    return {
      key: "document_closed",
      variant: "warning",
      icon: "icon-warning",
      title: "Documento encerrado",
      message: "Este documento foi encerrado e não está mais disponível para assinatura.",
    }
  }

  if (currentSignerCompletion(document, signer) === true) {
    if (document?.status === "certificating") {
      return {
        key: "document_processing",
        variant: "success",
        icon: "icon-check",
        title: "Sua assinatura já foi concluída",
        message:
          "O documento está sendo finalizado. Você receberá uma cópia por e-mail assim que estiver pronto.",
      }
    }

    if (document?.status === "pending_signature") {
      return {
        key: "signature_completed",
        variant: "success",
        icon: "icon-check",
        title: "Sua assinatura já foi concluída",
        message:
          "Você não precisa assinar novamente. O documento ainda aguarda as outras assinaturas e, quando todos concluírem, você receberá uma cópia por e-mail.",
      }
    }
  }

  if (document?.is_signer_access_closed) return documentUnavailableStatus()

  if (document?.status === "pending_signature") return null

  return documentUnavailableStatus()
}

const resolveKnownSigningLinkStatus = (document, signer) => {
  if (
    document?.status === "certificated" ||
    document?.is_closed ||
    document?.is_signer_access_closed
  ) {
    return resolveSigningLinkStatus(document, signer)
  }

  const signerCompletion = currentSignerCompletion(document, signer)

  if (document?.status === "pending_signature" && typeof signerCompletion === "boolean") {
    return resolveSigningLinkStatus(document, signer)
  }

  if (typeof document?.status === "string" && document.status !== "pending_signature") {
    return resolveSigningLinkStatus(document, signer)
  }

  return undefined
}

export const resolvePostVerificationStatus = async ({ document, signer, loadDocument }) => {
  const knownStatus = resolveKnownSigningLinkStatus(document, signer)

  if (typeof knownStatus !== "undefined") {
    return { document, status: knownStatus }
  }

  try {
    const fullDocument = await loadDocument()
    const fullDocumentStatus = resolveKnownSigningLinkStatus(fullDocument, signer)

    return {
      document: fullDocument,
      status: typeof fullDocumentStatus === "undefined" ? null : fullDocumentStatus,
    }
  } catch {
    return { document, status: null }
  }
}
